import { useState } from "react";
import axios from "axios";
import { VIJAYANAGAR_WARDS } from "./wardData";
import { ArrowRight, ArrowLeft, MapPin, HandHeart, Users, Sparkles, Vote, ShieldCheck, Sprout, Building2, Phone, Mail, X, CheckCircle2, Send } from "lucide-react";
import "./AuthorPage.css";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

const PORTRAIT = "/images/author-portrait.webp";

const COPY = {
  en: {
    back: "Back to Vijayanagar.org",
    constituency: "Vijayanagar Assembly Constituency",
    eyebrow: "✦ People. Ward. Progress.",
    tagline1: "Social worker. Neighbour first.",
    tagline2: "Independent Candidate for the next MLA elections.",
    lede: "For over a decade, SM Shailesh has walked every lane of Vijayanagar — listening more than he speaks, fixing what governments forgot, and organising residents around the small acts that add up to real change. Now, he is stepping forward as an",
    ledeBold: "Independent Candidate",
    ledeEnd: "— free of party lines, answerable only to you.",
    joinCta: "Join the movement",
    pledgeCta: "Read the pledge",
    stat1: ["10+", "Years of grassroots service"],
    stat2: ["35+", "Wards personally served"],
    stat3: ["1", "Independent voice for you"],
    portraitCaption: "Independent · No party. Only people.",
    quote: "\"I am not asking for a vote. I am asking for a partnership. A partnership between one neighbour who refuses to look away, and every family who calls Vijayanagar home.\"",
    quoteBy: "— SM Shailesh",
    pledgeKicker: "The Vijayanagar Pledge",
    pledgeHead: "Six promises. Ward by ward.",
    pledgeSub: "Each promise is measurable, dated, and tracked on this very platform. If we do not deliver, you will see it here first.",
    journeyKicker: "The road so far",
    journeyHead: "A decade before the ballot.",
    whyKicker: "Why Independent",
    whyHead: "The only party I belong to is Vijayanagar.",
    whyBody: "Independence is not a slogan. It is a promise not to be silenced by high commands, not to be traded away in coalitions, and not to be measured by anyone but the residents who live here. Every complaint on this platform, every clean-up drive, every night-school student is my whip.",
    pledges: [
      "No party ticket. No conditional loyalties.",
      "Public ward audits every 90 days.",
      "Open constituency office, six days a week.",
    ],
    connectKicker: "Get involved",
    connectHead: "Walk with us. Ward by ward.",
    connectSub: "Volunteer, host a corner-meet, or simply share what your street needs. Every small hand builds the bigger neighbourhood.",
    volunteerBtn: "Volunteer with SM Shailesh",
    disclaimer: "Published by supporters of SM Shailesh, Independent Candidate, Vijayanagar Assembly Constituency. This page is part of Vijayanagar.org — an open civic platform for residents.",
    pillars: [
      ["Civic Infrastructure", "Repair every broken road, streetlight and drain in every ward — visible progress on a public dashboard."],
      ["Health & Sanitation", "Weekly clean-up drives, ward-level clinics and a resident helpline that answers before an issue grows."],
      ["Youth & Employment", "Local skill centres, on-the-ground job fairs and a mentorship network built with Vijayanagar's own alumni."],
      ["Safety for All", "Well-lit streets, women-first response teams and a community watch that knows every corner by name."],
      ["Heritage & Culture", "Protect the stones that tell our story — restore public spaces, host free arts nights, celebrate our language."],
      ["Grassroots Governance", "Monthly ward Mohalla-sabhas. Every rupee tracked. Every decision made where the people live."],
    ],
    journey: [
      ["2011", "Started volunteering with local youth groups on civic clean-up drives."],
      ["2016", "Founded a resident welfare initiative supporting elderly citizens and school children across Vijayanagar wards."],
      ["2019", "Led sanitation, water access and streetlight repair campaigns coordinated ward by ward."],
      ["2023", "Ran health camps, sponsored night-school study centres, and mobilised flood-relief volunteers."],
      ["2026", "Filed intent to contest the next MLA elections as an Independent Candidate for Vijayanagar Assembly Constituency."],
    ],
    form: {
      title: "Join SM Shailesh's campaign",
      subtitle: "Volunteer sign-up",
      name: "Full name",
      phone: "Phone number",
      ward: "Ward / area you live in",
      email: "Email (optional)",
      message: "How would you like to help? (optional)",
      submit: "Sign me up",
      submitting: "Signing you up…",
      successTitle: "You're in. Welcome to the movement.",
      successBody: "Our ward coordinator will reach out to you shortly on the phone number you shared. Together, ward by ward.",
      wardPlaceholder: "e.g. Ward 12, Chandra Layout",
    },
    langNote: "ಕನ್ನಡ",
  },
  kn: {
    back: "ವಿಜಯನಗರ.org ಗೆ ಹಿಂತಿರುಗಿ",
    constituency: "ವಿಜಯನಗರ ವಿಧಾನಸಭಾ ಕ್ಷೇತ್ರ",
    eyebrow: "✦ ಜನ. ವಾರ್ಡ್. ಪ್ರಗತಿ.",
    tagline1: "ಸಮಾಜ ಸೇವಕ. ಮೊದಲು ನೆರೆಹೊರೆ.",
    tagline2: "ಮುಂಬರುವ ವಿಧಾನಸಭಾ ಚುನಾವಣೆಗೆ ಸ್ವತಂತ್ರ ಅಭ್ಯರ್ಥಿ.",
    lede: "ಒಂದು ದಶಕಕ್ಕೂ ಹೆಚ್ಚು ಕಾಲದಿಂದ, SM ಶೈಲೇಶ್ ವಿಜಯನಗರದ ಪ್ರತಿ ಬೀದಿಯನ್ನೂ ನಡೆದಿದ್ದಾರೆ — ಮಾತನಾಡುವುದಕ್ಕಿಂತ ಹೆಚ್ಚು ಕೇಳಿಸಿಕೊಳ್ಳುತ್ತಾ, ಸರಕಾರಗಳು ಮರೆತಿದ್ದನ್ನು ಸರಿಪಡಿಸುತ್ತಾ, ಚಿಕ್ಕ ಸಣ್ಣ ಕಾರ್ಯಗಳ ಸುತ್ತ ನಿವಾಸಿಗಳನ್ನು ಸಂಘಟಿಸುತ್ತಾ ಬಂದಿದ್ದಾರೆ. ಈಗ ಅವರು ಮುಂದೆ ಬರುತ್ತಿದ್ದಾರೆ",
    ledeBold: "ಸ್ವತಂತ್ರ ಅಭ್ಯರ್ಥಿಯಾಗಿ",
    ledeEnd: "— ಪಕ್ಷದ ಕಟ್ಟುಪಾಡುಗಳಿಂದ ಮುಕ್ತರಾಗಿ, ಕೇವಲ ನಿಮಗೆ ಉತ್ತರದಾಯಿ.",
    joinCta: "ಚಳವಳಿಗೆ ಸೇರಿ",
    pledgeCta: "ಪ್ರತಿಜ್ಞೆಯನ್ನು ಓದಿ",
    stat1: ["10+", "ವರ್ಷಗಳ ಜನ ಸೇವೆ"],
    stat2: ["35+", "ಸೇವೆ ಸಲ್ಲಿಸಿದ ವಾರ್ಡ್‌ಗಳು"],
    stat3: ["1", "ನಿಮಗಾಗಿ ಒಂದೇ ಸ್ವತಂತ್ರ ಧ್ವನಿ"],
    portraitCaption: "ಸ್ವತಂತ್ರ · ಪಕ್ಷವಿಲ್ಲ. ಜನರೇ ಸಾಕು.",
    quote: "\"ನಾನು ಮತ ಕೇಳುತ್ತಿಲ್ಲ. ನಾನು ಪಾಲುದಾರಿಕೆ ಕೇಳುತ್ತಿದ್ದೇನೆ. ಒಳಗಿನ ನೋವನ್ನು ಕಡೆಗಣಿಸಲಾರದ ಒಬ್ಬ ನೆರೆಯವರಿಗೂ, ವಿಜಯನಗರವನ್ನು ಮನೆ ಎಂದು ಕರೆಯುವ ಪ್ರತಿ ಕುಟುಂಬಕ್ಕೂ ನಡುವಿನ ಪಾಲುದಾರಿಕೆ.\"",
    quoteBy: "— SM ಶೈಲೇಶ್",
    pledgeKicker: "ವಿಜಯನಗರ ಪ್ರತಿಜ್ಞೆ",
    pledgeHead: "ಆರು ಭರವಸೆಗಳು. ವಾರ್ಡ್‌ವಾರು.",
    pledgeSub: "ಪ್ರತಿ ಭರವಸೆ ಅಳೆಯಬಹುದಾದದ್ದು, ದಿನಾಂಕ ನಿಗದಿಪಡಿಸಿದ್ದು, ಈ ವೇದಿಕೆಯಲ್ಲೇ ಟ್ರ್ಯಾಕ್ ಆಗುತ್ತಿದೆ. ನಾವು ಇವನ್ನು ಪೂರೈಸದಿದ್ದರೆ, ಮೊದಲಿಗೆ ಇಲ್ಲೇ ನೋಡುತ್ತೀರಿ.",
    journeyKicker: "ಇಲ್ಲಿಯವರೆಗಿನ ಹಾದಿ",
    journeyHead: "ಚುನಾವಣೆಗೂ ಮುನ್ನ ಒಂದು ದಶಕ.",
    whyKicker: "ಏಕೆ ಸ್ವತಂತ್ರ",
    whyHead: "ನಾನು ಸೇರಿರುವ ಏಕೈಕ ಪಕ್ಷ ವಿಜಯನಗರ.",
    whyBody: "ಸ್ವತಂತ್ರತೆ ಒಂದು ಘೋಷಣೆ ಅಲ್ಲ. ಇದು ಹೈಕಮಾಂಡ್‌ಗಳ ಆದೇಶಕ್ಕೆ ಬಾಗದ, ಒಕ್ಕೂಟಗಳಲ್ಲಿ ಅದಲುಬದಲಾಗದ, ಇಲ್ಲಿಯ ನಿವಾಸಿಗಳಿಂದ ಮಾತ್ರ ಅಳೆಯಲ್ಪಡುವ ಭರವಸೆ. ಈ ವೇದಿಕೆಯಲ್ಲಿನ ಪ್ರತಿ ದೂರು, ಪ್ರತಿ ಸ್ವಚ್ಛತಾ ಕಾರ್ಯ, ಪ್ರತಿ ರಾತ್ರಿಶಾಲಾ ವಿದ್ಯಾರ್ಥಿಯೇ ನನ್ನ ಚಾಟಿ.",
    pledges: [
      "ಪಕ್ಷದ ಟಿಕೆಟ್ ಇಲ್ಲ. ಷರತ್ತಿನ ನಿಷ್ಠೆಯೂ ಇಲ್ಲ.",
      "ಪ್ರತಿ 90 ದಿನಗಳಿಗೊಮ್ಮೆ ಸಾರ್ವಜನಿಕ ವಾರ್ಡ್ ಆಡಿಟ್.",
      "ವಾರದ ಆರು ದಿನ ತೆರೆದ ಕ್ಷೇತ್ರ ಕಚೇರಿ.",
      "ಪ್ರತಿ ದೇಣಿಗೆ ಮತ್ತು ಖರ್ಚು ಸಾರ್ವಜನಿಕ ದಾಖಲೆಯಲ್ಲಿ.",
    ],
    connectKicker: "ಒಳಗೊಳ್ಳಿ",
    connectHead: "ನಮ್ಮೊಂದಿಗೆ ನಡೆಯಿರಿ. ವಾರ್ಡ್‌ವಾರು.",
    connectSub: "ಸ್ವಯಂಸೇವಕರಾಗಿ, ಮೂಲೆ-ಸಭೆ ಆಯೋಜಿಸಿ, ಅಥವಾ ನಿಮ್ಮ ಬೀದಿಗೆ ಏನು ಬೇಕು ಎಂದು ಹೇಳಿ. ಪ್ರತಿ ಚಿಕ್ಕ ಕೈಯೂ ದೊಡ್ಡ ನೆರೆಹೊರೆಯನ್ನು ಕಟ್ಟುತ್ತದೆ.",
    volunteerBtn: "SM ಶೈಲೇಶ್ ಜೊತೆ ಸ್ವಯಂಸೇವಕರಾಗಿ",
    disclaimer: "SM ಶೈಲೇಶ್, ಸ್ವತಂತ್ರ ಅಭ್ಯರ್ಥಿ, ವಿಜಯನಗರ ವಿಧಾನಸಭಾ ಕ್ಷೇತ್ರದ ಬೆಂಬಲಿಗರಿಂದ ಪ್ರಕಟಿಸಲಾಗಿದೆ. ಈ ಪುಟ Vijayanagar.org ನ ಭಾಗ — ನಿವಾಸಿಗಳಿಗಾಗಿ ಒಂದು ಮುಕ್ತ ನಾಗರಿಕ ವೇದಿಕೆ.",
    pillars: [
      ["ನಾಗರಿಕ ಮೂಲಸೌಕರ್ಯ", "ಪ್ರತಿ ವಾರ್ಡ್‌ನಲ್ಲಿ ಒಡೆದ ಪ್ರತಿ ರಸ್ತೆ, ಬೀದಿದೀಪ ಮತ್ತು ಚರಂಡಿಯನ್ನು ಸರಿಪಡಿಸಿ — ಸಾರ್ವಜನಿಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ನಲ್ಲಿ ಗೋಚರ ಪ್ರಗತಿ."],
      ["ಆರೋಗ್ಯ ಮತ್ತು ನೈರ್ಮಲ್ಯ", "ವಾರದ ಸ್ವಚ್ಛತಾ ಕಾರ್ಯಗಳು, ವಾರ್ಡ್ ಮಟ್ಟದ ಚಿಕಿತ್ಸಾಲಯಗಳು ಮತ್ತು ಸಮಸ್ಯೆ ಬೆಳೆಯುವ ಮೊದಲೇ ಉತ್ತರಿಸುವ ನಿವಾಸಿ ಸಹಾಯವಾಣಿ."],
      ["ಯುವಜನ ಮತ್ತು ಉದ್ಯೋಗ", "ಸ್ಥಳೀಯ ಕೌಶಲ್ಯ ಕೇಂದ್ರಗಳು, ನೆಲದಲ್ಲಿ ಉದ್ಯೋಗ ಮೇಳಗಳು ಮತ್ತು ವಿಜಯನಗರದ ಹಿರಿಯ ವಿದ್ಯಾರ್ಥಿಗಳಿಂದಲೇ ಕಟ್ಟಿದ ಮಾರ್ಗದರ್ಶಿ ಜಾಲ."],
      ["ಎಲ್ಲರಿಗೂ ಸುರಕ್ಷತೆ", "ಚೆನ್ನಾಗಿ ಬೆಳಗಿದ ರಸ್ತೆಗಳು, ಮಹಿಳಾ-ಆದ್ಯತೆಯ ಸ್ಪಂದನ ತಂಡಗಳು ಮತ್ತು ಪ್ರತಿ ಮೂಲೆಯನ್ನೂ ಹೆಸರಿನಿಂದ ಗುರುತಿಸುವ ಸಮುದಾಯ ಕಾವಲು."],
      ["ಪರಂಪರೆ ಮತ್ತು ಸಂಸ್ಕೃತಿ", "ನಮ್ಮ ಕಥೆ ಹೇಳುವ ಕಲ್ಲುಗಳನ್ನು ಕಾಪಾಡಿ — ಸಾರ್ವಜನಿಕ ಸ್ಥಳಗಳನ್ನು ಪುನಃಸ್ಥಾಪಿಸಿ, ಉಚಿತ ಕಲಾ ಸಂಜೆಗಳನ್ನು ಆಯೋಜಿಸಿ, ನಮ್ಮ ಭಾಷೆಯನ್ನು ಆಚರಿಸಿ."],
      ["ತಳಮಟ್ಟದ ಆಡಳಿತ", "ಪ್ರತಿ ತಿಂಗಳು ವಾರ್ಡ್ ಮೊಹಲ್ಲಾ ಸಭೆಗಳು. ಪ್ರತಿ ರೂಪಾಯಿ ಟ್ರ್ಯಾಕ್. ಪ್ರತಿ ನಿರ್ಧಾರ ಜನರು ವಾಸಿಸುವಲ್ಲಿಯೇ."],
    ],
    journey: [
      ["2011", "ಸ್ಥಳೀಯ ಯುವಸಂಘಟನೆಗಳೊಂದಿಗೆ ನಾಗರಿಕ ಸ್ವಚ್ಛತಾ ಕಾರ್ಯಗಳಲ್ಲಿ ಸ್ವಯಂಸೇವೆ ಪ್ರಾರಂಭ."],
      ["2016", "ವಿಜಯನಗರದ ವಾರ್ಡ್‌ಗಳಾದ್ಯಂತ ಹಿರಿಯ ನಾಗರಿಕರು ಮತ್ತು ಶಾಲಾ ಮಕ್ಕಳಿಗೆ ಬೆಂಬಲ ನೀಡುವ ನಿವಾಸಿ ಕಲ್ಯಾಣ ಉಪಕ್ರಮ ಸ್ಥಾಪನೆ."],
      ["2019", "ವಾರ್ಡ್‌ವಾರು ಸಂಘಟಿತವಾದ ನೈರ್ಮಲ್ಯ, ನೀರಿನ ಪ್ರವೇಶ ಮತ್ತು ಬೀದಿದೀಪ ದುರಸ್ತಿ ಅಭಿಯಾನಗಳ ನೇತೃತ್ವ."],
      ["2023", "ಆರೋಗ್ಯ ಶಿಬಿರಗಳು, ರಾತ್ರಿ-ಶಾಲಾ ಅಧ್ಯಯನ ಕೇಂದ್ರಗಳ ಪ್ರಾಯೋಜಕತೆ ಮತ್ತು ಪ್ರವಾಹ ಪರಿಹಾರ ಸ್ವಯಂಸೇವಕರ ಸಜ್ಜುಗೊಳಿಸುವಿಕೆ."],
      ["2026", "ವಿಜಯನಗರ ವಿಧಾನಸಭಾ ಕ್ಷೇತ್ರಕ್ಕೆ ಸ್ವತಂತ್ರ ಅಭ್ಯರ್ಥಿಯಾಗಿ ಮುಂಬರುವ ಚುನಾವಣೆಯಲ್ಲಿ ಸ್ಪರ್ಧಿಸುವ ಉದ್ದೇಶ ಸಲ್ಲಿಕೆ."],
    ],
    form: {
      title: "SM ಶೈಲೇಶ್ ಅಭಿಯಾನಕ್ಕೆ ಸೇರಿ",
      subtitle: "ಸ್ವಯಂಸೇವಕರ ನೋಂದಣಿ",
      name: "ಪೂರ್ಣ ಹೆಸರು",
      phone: "ಫೋನ್ ಸಂಖ್ಯೆ",
      ward: "ನೀವು ವಾಸಿಸುವ ವಾರ್ಡ್ / ಪ್ರದೇಶ",
      email: "ಇಮೇಲ್ (ಐಚ್ಛಿಕ)",
      message: "ನೀವು ಹೇಗೆ ಸಹಾಯ ಮಾಡಲು ಇಷ್ಟಪಡುತ್ತೀರಿ? (ಐಚ್ಛಿಕ)",
      submit: "ನನ್ನನ್ನು ಸೇರಿಸಿ",
      submitting: "ನೋಂದಾಯಿಸುತ್ತಿದೆ…",
      successTitle: "ಸೇರಿದ್ದೀರಿ. ಚಳವಳಿಗೆ ಸ್ವಾಗತ.",
      successBody: "ನೀವು ಹಂಚಿಕೊಂಡ ಫೋನ್ ಸಂಖ್ಯೆಗೆ ನಮ್ಮ ವಾರ್ಡ್ ಸಂಯೋಜಕರು ಶೀಘ್ರದಲ್ಲೇ ಸಂಪರ್ಕಿಸುತ್ತಾರೆ. ಒಟ್ಟಾಗಿ, ವಾರ್ಡ್‌ವಾರು.",
      wardPlaceholder: "ಉದಾ. ವಾರ್ಡ್ 12, ಚಂದ್ರ ಬಡಾವಣೆ",
    },
    langNote: "English",
  },
};

const PILLAR_ICONS = [Building2, Sprout, Users, ShieldCheck, Sparkles, HandHeart];

export default function AuthorPage({ onBack, lang = "en", onToggleLang }) {
  const t = COPY[lang] || COPY.en;
  const [showForm, setShowForm] = useState(false);
  const openForm = () => setShowForm(true);

  return (
    <div className="author-page" data-testid="author-page" lang={lang === "kn" ? "kn" : "en"}>
      <header className="author-topbar">
        <button className="ghost-btn" onClick={() => { window.location.href = "/"; }} data-testid="author-back-button">
          <ArrowLeft size={16} /> {t.back}
        </button>
        <div className="author-topbar-right">
          {onToggleLang && (
            <button className="ghost-btn" onClick={onToggleLang} data-testid="author-lang-toggle">
              {t.langNote}
            </button>
          )}
          <span className="author-badge" data-testid="author-constituency"><MapPin size={14} /> {t.constituency}</span>
        </div>
      </header>

      <section className="author-hero">
        <div className="author-hero-copy">
          <p className="kicker" data-testid="author-eyebrow">{t.eyebrow}</p>
          <h1 data-testid="author-name">SM Shailesh</h1>
          <p className="author-tagline" data-testid="author-tagline">
            {t.tagline1} <br /> {t.tagline2}
          </p>
          <p className="author-lede">
            {t.lede} <b>{t.ledeBold}</b> {t.ledeEnd}
          </p>
          <div className="author-cta-row">
            <button className="author-primary" onClick={openForm} data-testid="author-volunteer-cta">
              <HandHeart size={17} /> {t.joinCta}
            </button>
            <a href="#pillars" className="author-secondary" data-testid="author-pillars-cta">
              {t.pledgeCta} <ArrowRight size={16} />
            </a>
          </div>
          <div className="author-mini-stats" data-testid="author-mini-stats">
            <div><b>{t.stat1[0]}</b><span>{t.stat1[1]}</span></div>
            <div><b>{t.stat2[0]}</b><span>{t.stat2[1]}</span></div>
            <div><b>{t.stat3[0]}</b><span>{t.stat3[1]}</span></div>
          </div>
        </div>
        <div className="author-hero-frame">
          <div className="author-portrait-glow" aria-hidden="true" />
          <img src={PORTRAIT} alt="SM Shailesh, Independent Candidate for Vijayanagar Assembly Constituency" data-testid="author-portrait" />
          <div className="author-portrait-caption"><span className="dot" /> {t.portraitCaption}</div>
        </div>
      </section>

      <section className="author-quote" data-testid="author-quote-section">
        <blockquote>{t.quote}</blockquote>
        <cite>{t.quoteBy}</cite>
      </section>

      <section className="author-pillars" id="pillars">
        <div className="author-section-head">
          <p className="kicker">{t.pledgeKicker}</p>
          <h2>{t.pledgeHead}</h2>
          <p>{t.pledgeSub}</p>
        </div>
        <div className="pillar-grid">
          {t.pillars.map(([title, body], i) => {
            const Icon = PILLAR_ICONS[i] || Sparkles;
            return (
              <article className="pillar-card" key={title} data-testid={`author-pillar-${i}`}>
                <div className="pillar-icon"><Icon size={20} /></div>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="author-journey">
        <div className="author-section-head">
          <p className="kicker">{t.journeyKicker}</p>
          <h2>{t.journeyHead}</h2>
        </div>
        <ol className="journey-line" data-testid="author-journey">
          {t.journey.map(([year, event], i) => (
            <li key={year} data-testid={`author-journey-${i}`}>
              <span className="journey-year">{year}</span>
              <p>{event}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="author-why-independent" data-testid="author-why-independent">
        <div>
          <p className="kicker"><Vote size={13} /> {t.whyKicker}</p>
          <h2>{t.whyHead}</h2>
          <p>{t.whyBody}</p>
        </div>
        <div className="pledge-list">
          {t.pledges.map((p, i) => (
            <div key={i}><ShieldCheck size={16} /> {p}</div>
          ))}
        </div>
      </section>

      <section className="author-connect" data-testid="author-connect">
        <div className="author-section-head center">
          <p className="kicker">{t.connectKicker}</p>
          <h2>{t.connectHead}</h2>
          <p>{t.connectSub}</p>
        </div>
        <div className="connect-actions">
          <button className="author-primary" onClick={openForm} data-testid="author-connect-volunteer">
            <HandHeart size={16} /> {t.volunteerBtn}
          </button>
          <a className="author-secondary" href="mailto:campaign@vijayanagar.org" data-testid="author-connect-email">
            <Mail size={16} /> campaign@vijayanagar.org
          </a>
          <a className="author-secondary" href="tel:+918550855030" data-testid="author-connect-phone">
            <Phone size={16} /> Ward office helpline
          </a>
        </div>
        <p className="author-disclaimer" data-testid="author-disclaimer">{t.disclaimer}</p>
      </section>

      {showForm && <VolunteerForm copy={t.form} onClose={() => setShowForm(false)} />}
    </div>
  );
}

function VolunteerForm({ copy, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", ward: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting"); setError("");
    try {
      await api.post("/campaign/volunteers", form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={onClose} data-testid="volunteer-form-modal">
      <div className="modal author-volunteer-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} data-testid="volunteer-form-close"><X /></button>

        {status === "success" ? (
          <div className="volunteer-success" data-testid="volunteer-form-success">
            <div className="volunteer-success-icon">
              <CheckCircle2 size={42} strokeWidth={2} />
            </div>

            <div className="volunteer-success-content">
              <h3>{copy.successTitle}</h3>
              <p>{copy.successBody}</p>
            </div>

            <button
              type="button"
              className="volunteer-success-done"
              onClick={onClose}
              data-testid="volunteer-form-done"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="kicker">{copy.subtitle}</p>
            <h2>{copy.title}</h2>
            <form onSubmit={submit} className="volunteer-form">
              <label className="field">
                <span>{copy.name}</span>
                <input value={form.name} onChange={change("name")} required minLength={2} data-testid="volunteer-name-input" />
              </label>
              <label className="field">
                <span>{copy.phone}</span>
                <input value={form.phone} onChange={change("phone")} required inputMode="tel" pattern="[0-9+\-\s]{6,20}" data-testid="volunteer-phone-input" />
              </label>
              <label className="field">
				  <span>{copy.ward}</span>
				  <select
					value={form.ward}
					onChange={change("ward")}
					required
					data-testid="volunteer-ward-input"
				  >
					<option value="">Select your Ward / Area</option>
					{VIJAYANAGAR_WARDS.map((ward) => (
					  <option key={ward.number} value={`${ward.number} — ${ward.name}`}>
						{ward.number} — {ward.name}
					  </option>
					))}
				  </select>
				</label>
              <label className="field">
                <span>{copy.email}</span>
                <input type="email" value={form.email} onChange={change("email")} data-testid="volunteer-email-input" />
              </label>
              <label className="field">
                <span>{copy.message}</span>
                <textarea rows={3} value={form.message} onChange={change("message")} data-testid="volunteer-message-input" />
              </label>
              {error && <p className="error" data-testid="volunteer-form-error">{error}</p>}
              <button className="primary-btn full" disabled={status === "submitting"} data-testid="volunteer-form-submit">
                {status === "submitting" ? copy.submitting : copy.submit} <Send size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
